/**
 * Cookie consent store — SSR-safe.
 *
 * Cookie name : lml_cookie_consent
 * Cookie shape: { version, timestamp, analytics: bool, marketing: bool }
 *
 * Uses document.cookie directly (same pattern as the rest of this codebase).
 * All reads/writes no-op server-side.
 */

export const CONSENT_VERSION = 1;

const COOKIE_NAME = 'lml_cookie_consent';

let subscribers = [];

const isServer = () => typeof window === 'undefined';

const parseCookieValue = name => {
  if (isServer()) return null;
  try {
    const match = document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith(name + '='));
    if (!match) return null;
    return JSON.parse(decodeURIComponent(match.slice(name.length + 1)));
  } catch {
    return null;
  }
};

const writeCookie = (name, value) => {
  if (isServer()) return;
  const encoded = encodeURIComponent(JSON.stringify(value));
  const maxAge = 365 * 24 * 60 * 60;
  document.cookie = `${name}=${encoded}; max-age=${maxAge}; path=/; SameSite=Lax`;
};

/**
 * Returns the stored consent record if the version is current, otherwise null.
 * A null return means the user hasn't decided yet (or the record is stale).
 */
export const getConsent = () => {
  const stored = parseCookieValue(COOKIE_NAME);
  if (!stored || !stored.version || stored.version < CONSENT_VERSION) return null;
  return stored;
};

/**
 * Persist the user's consent choices and notify all subscribers.
 * @param {{ analytics: boolean, marketing: boolean }} choices
 */
export const setConsent = ({ analytics, marketing }) => {
  const record = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
  };
  writeCookie(COOKIE_NAME, record);
  subscribers.forEach(cb => {
    try {
      cb(record);
    } catch (_) {}
  });
};

/**
 * Returns true if the given category is consented to.
 * 'necessary' is always true. 'analytics' / 'marketing' require a stored decision.
 */
export const hasConsent = category => {
  if (category === 'necessary') return true;
  const record = getConsent();
  if (!record) return false;
  return Boolean(record[category]);
};

/** Returns true if the user has made a consent choice (any version-current record exists). */
export const hasDecided = () => getConsent() !== null;

/**
 * Subscribe to consent changes.
 * @param {function} callback  Called with the new consent record whenever setConsent() is called.
 * @returns {function} Unsubscribe function.
 */
export const onConsentChange = callback => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter(cb => cb !== callback);
  };
};
